import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Phone, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '2348034494507';
const SESSION_KEY = 'geopick_chat_session';

const OPENING_MESSAGE = {
  role: 'assistant',
  content: "Welcome to Versatile Geopick. I'm your GeoPick Assistant. I can help you find the right service, verify land, request a quotation, or connect you with our team. How can I help you today?"
};

const QUICK_ACTIONS = [
  { label: 'Request a Survey', message: 'I need a land survey' },
  { label: 'Verify a Property', message: 'I want to verify a property before buying' },
  { label: 'Request a Quote', message: 'I would like to request a quote' },
  { label: 'Speak to a Consultant', message: 'I would like to speak to a consultant' }
];

const getSessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

const TypingIndicator = () => (
  <div className="flex items-center gap-1 px-4 py-3 bg-stone-100 rounded-2xl rounded-bl-sm w-fit" data-testid="chat-typing-indicator">
    {[0, 1, 2].map((i) => (
      <motion.span
        key={i}
        className="w-2 h-2 rounded-full bg-stone-400"
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
      />
    ))}
  </div>
);

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([OPENING_MESSAGE]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const loadHistory = async () => {
      try {
        const res = await fetch(`${API}/api/chat/history/${getSessionId()}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
          setShowQuickActions(false);
        }
      } catch (e) {
        // history is optional
      }
    };
    loadHistory();
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 350);
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed || isTyping) return;
    setShowQuickActions(false);
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setIsTyping(true);
    try {
      const res = await fetch(`${API}/api/chat/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId(), message: trimmed })
      });
      if (!res.ok) throw new Error('Chat request failed');
      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.response }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: "I'm having trouble connecting right now. Please reach us directly on WhatsApp at +234 803 449 4507 and we'll assist you immediately." }
      ]);
    } finally {
      setIsTyping(false);
    }
  }, [isTyping]);

  const openWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello Versatile Geopick, I need assistance.')}`, '_blank');
  };

  return (
    <>
      {/* Floating toggle button */}
      <motion.button
        data-testid="chat-widget-toggle"
        onClick={() => setIsOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        whileTap={{ scale: 0.9 }}
        aria-label="Open chat"
      >
        <span className="absolute inset-0 rounded-full bg-primary animate-ping opacity-20 pointer-events-none" />
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={isOpen ? 'close' : 'open'}
            initial={{ rotate: -90, opacity: 0 }}
            animate={{ rotate: 0, opacity: 1 }}
            exit={{ rotate: 90, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          </motion.span>
        </AnimatePresence>
      </motion.button>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[calc(100vw-2rem)] sm:w-[380px] h-[560px] max-h-[calc(100vh-8rem)] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-primary text-primary-foreground px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center font-semibold text-lg">
                G
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium leading-tight" data-testid="chat-header-title">GeoPick Assistant</p>
                <p className="text-xs text-primary-foreground/80 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 inline-block" />
                  Online · replies instantly
                </p>
              </div>
              <button
                data-testid="chat-whatsapp-button"
                onClick={openWhatsApp}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 transition-colors flex items-center justify-center"
                aria-label="Continue on WhatsApp"
                title="Continue on WhatsApp"
              >
                <Phone className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#faf8f5]" data-testid="chat-messages-area">
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    data-testid={`chat-message-${msg.role}-${i}`}
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-sm'
                        : 'bg-white text-stone-700 border border-stone-200 rounded-2xl rounded-bl-sm shadow-sm'
                    }`}
                  >
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div className="flex justify-start"><TypingIndicator /></div>
              )}

              {showQuickActions && !isTyping && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-quick-actions">
                  {QUICK_ACTIONS.map((qa) => (
                    <button
                      key={qa.label}
                      data-testid={`chat-quick-action-${qa.label.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => sendMessage(qa.message)}
                      className="text-xs px-3 py-2 rounded-full border border-primary/30 text-primary bg-white hover:bg-primary hover:text-primary-foreground transition-colors"
                    >
                      {qa.label}
                    </button>
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form
              className="border-t border-stone-200 bg-white px-3 py-3 flex items-center gap-2"
              onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
            >
              <input
                ref={inputRef}
                data-testid="chat-input"
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 text-sm bg-stone-100 rounded-full px-4 py-2.5 outline-none focus:ring-2 focus:ring-primary/40 transition-shadow"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={!input.trim() || isTyping}
                className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-40 hover:scale-105 active:scale-95 transition-transform"
                aria-label="Send message"
              >
                {isTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
